import './jahresabschluss.css';
import { useEffect } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api';
import { t, tx } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { Loading } from '../belege/Pages';
import { useNotifications } from '../belege/shared';
import { useClientApp } from '../context';
import { DokSheetHost } from '../dokumente/DokSheet';
import { usePage, type Crumb } from '../page';
import { jaPath } from './labels';
import type { JaResponse } from './types';

// Bereich Jahresabschluss (wie in der Demo): Firma wählen → je Geschäftsjahr die Dokumente, die die Kanzlei vom
// Steuerberater hochgeladen hat (Bilanz, GuV, Erklärungen …). Ein Dokument öffnet sich als Fenster (?dok=<id>).
export function JaRoutes() {
  return (
    <Routes>
      <Route index element={<FirmsPage />} />
      <Route path=":clientId" element={<Level />} />
      <Route path="*" element={<Navigate to={jaPath()} replace />} />
    </Routes>
  );
}

function Level() {
  const { clients } = useClientApp();
  const clientId = useParams().clientId ?? '';
  if (!clients.some((c) => c.id === clientId)) return <Navigate to={jaPath()} replace />;
  return (
    <>
      <FirmPage clientId={clientId} />
      <DokSheetHost />
    </>
  );
}

// Titel, Pfad und Zurück: Start › Jahresabschluss › <Firma>; mit nur einer Firma ohne Firmenwahl
function useJaPage(clientName?: string) {
  const { clients } = useClientApp();
  const one = clients.length === 1;
  const crumbs: Crumb[] = [
    { label: t('Start'), to: '/app' },
    { label: t('Jahresabschluss'), to: clientName && !one ? jaPath() : undefined },
  ];
  if (clientName) crumbs.push({ label: clientName });
  usePage({ title: t('Jahresabschluss'), crumbs, back: clientName && !one ? jaPath() : '/app', nav: 'jahresabschluss' });
}

// Jahresabschluss · Für welche Firma? (nur mit mehreren Firmen)
function FirmsPage() {
  const { clients } = useClientApp();
  useJaPage();
  if (clients.length === 1) return <Navigate to={jaPath(clients[0]!.id)} replace />;
  return (
    <>
      <div className="hello">
        <h2>{t('Jahresabschluss')}</h2>
        <p>{t('Für welche Firma?')}</p>
      </div>
      <div className="list">
        {clients.map((c) => (
          <Link key={c.id} className="item" to={jaPath(c.id)}>
            <span className="ico" aria-hidden="true">
              {c.name.charAt(0)}
            </span>
            <span className="tx">
              <b>{c.name}</b>
              <small>{t('Bilanz, GuV und Steuererklärungen pro Jahr')}</small>
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}

function FirmPage({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const { clientName } = useClientApp();
  const notes = useNotifications();
  const reloadNotes = notes.reload;
  const res = useApi<JaResponse>(`/api/app/jahresabschluss/${clientId}`, { live: ['docs', 'clients'], clientId });
  const d = res.data;
  useJaPage(d?.client.name ?? clientName(clientId));

  // Die Meldungen „Jahresabschluss …“ dieser Firma gelten als gelesen, sobald der Bereich offen ist
  const unread = (notes.data?.items ?? []).some((n) => n.type === 'jahresabschluss' && !n.read && n.clientId === clientId);
  useEffect(() => {
    if (unread) {
      api
        .post(`/api/app/jahresabschluss/${clientId}/read`)
        .then(() => reloadNotes())
        .catch(() => {});
    }
  }, [unread, clientId, reloadNotes]);

  return (
    <>
      <div className="hello">
        <h2>{t('Jahresabschluss')}</h2>
        <p>{d ? t('{name} · von Ihrer Kanzlei', { name: d.client.name }) : ''}</p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : d.years.length ? (
        d.years.map((y) => (
          <div className="files jafiles" key={y.year}>
            <h3>
              {y.docs.length === 1
                ? t('Geschäftsjahr {year} (1 Dokument)', { year: y.year })
                : t('Geschäftsjahr {year} ({n} Dokumente)', { year: y.year, n: y.docs.length })}
            </h3>
            {y.docs.map((x) => (
              <button
                key={x.id}
                type="button"
                className="file"
                onClick={() => navigate({ search: `?dok=${x.id}` }, { state: { sheet: true } })}
              >
                <span>
                  {tx(x.label)}
                  <small className="fsub">
                    {x.file.name} · {t('am {date}', { date: fmtDate(x.uploadedAt) })}
                  </small>
                </span>
                <span className="ok">
                  {t('Ansehen')} <span className="chev">›</span>
                </span>
              </button>
            ))}
          </div>
        ))
      ) : (
        <div className="files">
          <div className="empty jaempty">
            {t(
              'Noch keine Jahresabschlüsse. Sobald Ihre Kanzlei einen hochlädt, finden Sie hier Bilanz, GuV und Steuererklärungen.',
            )}
          </div>
        </div>
      )}
    </>
  );
}
